import React, { useState } from "react";
import { observer } from "mobx-react";
import { useChecklistIndex } from "./ProgressBlock";

// Irregular past tenses, keyed by the -ing form the step label starts with.
const IRREGULAR_PAST = {
  running: "ran", finding: "found", getting: "got", building: "built",
  sending: "sent", setting: "set", writing: "wrote", reading: "read",
  making: "made", taking: "took", thinking: "thought", putting: "put",
};

// "Searching prospects" -> "Searched prospects" for the collapsed summary.
export const pastTense = label =>
  String(label || "").replace(/^(\w+)ing\b/i, (word, stem) => {
    const irregular = IRREGULAR_PAST[word.toLowerCase()];
    if (!irregular) return `${stem}ed`; // stopp-ed, sav-ed, creat-ed
    return /^[A-Z]/.test(word) ? irregular[0].toUpperCase() + irregular.slice(1) : irregular;
  });

// Collapsed summaries get the first clause of the detail, never a paragraph
// of tool output.
const shortDetail = d => {
  const first = String(d || "").split(/(?<=[.!?])\s/)[0].trim();
  return first.length > 48 ? `${first.slice(0, 46).trim()}…` : first;
};

const StepRow = observer(({ step, live }) => {
  const running = step.status === "running";
  const messages = step.messages || [];
  const checklist = running && messages.length > 1;
  const current = useChecklistIndex(messages.length, checklist);
  return (
    <li className={`copilotActivity__step is-${step.status || "running"}${live ? " is-live" : ""}`}>
      <span className="copilotActivity__dot">
        {step.status === "done" && <i className="material-icons-round">check</i>}
        {step.status === "failed" && <i className="material-icons-round">remove</i>}
      </span>
      <div className="copilotActivity__body">
        <div className="copilotActivity__line">
          {/* A finished step read "Running list inbox" under the answer; it ran, so say so. */}
          <span className="copilotActivity__label">{running ? step.label : pastTense(step.label)}</span>
          {step.detail && step.status !== "failed" && (
            <span className="copilotActivity__detail" title={step.detail}>
              {shortDetail(step.detail)}
            </span>
          )}
        </div>
        {checklist && (
          <ul className="copilotActivity__checklist">
            {messages.map((msg, i) => {
              const state = i < current ? "done" : i === current ? "active" : "todo";
              return (
                <li className={`copilotActivity__check is-${state}`} key={msg}>
                  <i className="material-icons-outlined">
                    {state === "done" ? "check_circle" : "radio_button_unchecked"}
                  </i>
                  <span>{msg}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </li>
  );
});

/**
 * A turn's tool steps as one activity rail: a dot per step, label left and
 * result right, the live step pulsing at the bottom. Once every step is done
 * and the turn has ended it collapses to a single muted summary so the result
 * and the prose carry the weight; click to expand.
 */
const ActivityGroup = observer(({ steps, turnStreaming }) => {
  const [open, setOpen] = useState(false);
  const visible = steps.filter(s => !s.hidden);
  if (!visible.length) return null;
  const allDone = visible.every(s => s.status === "done");
  const failed = visible.some(s => s.status === "failed");
  const collapsed = allDone && !turnStreaming && !open && visible.length > 0;

  if (collapsed) {
    const last = visible[visible.length - 1];
    // Raw tool runs ("Running list sequences") mean nothing to the reader; say what they amount to.
    const toolRuns = visible.length > 1 && visible.every(s => /^running\b/i.test(s.label || ""));
    const summary =
      visible.length === 1 && last.detail
        ? `${pastTense(last.label)} · ${shortDetail(last.detail)}`
        : toolRuns
        ? "Checked your account"
        : visible.map(s => pastTense(s.label)).join(" · ");
    // One step has nothing more to show: a plain line, no count or chevron.
    if (visible.length === 1) {
      return (
        <div className="copilotActivity copilotActivity--collapsed">
          <i className="material-icons-round copilotActivity__tick">check</i>
          <span className="copilotActivity__summary">{summary}</span>
        </div>
      );
    }
    return (
      <button
        type="button"
        className="copilotActivity copilotActivity--collapsed"
        onClick={() => setOpen(true)}
      >
        <i className="material-icons-round copilotActivity__tick">check</i>
        <span className="copilotActivity__summary">{summary}</span>
        <span className="copilotActivity__count">{visible.length} steps</span>
        <i className="material-icons-round copilotActivity__chev">expand_more</i>
      </button>
    );
  }

  return (
    <div className={`copilotActivity${failed ? " copilotActivity--failed" : ""}`}>
      {allDone && !turnStreaming && (
        <button type="button" className="copilotActivity__collapse" onClick={() => setOpen(false)}>
          <i className="material-icons-round">expand_less</i>
        </button>
      )}
      <ul className="copilotActivity__steps">
        {visible.map((step, i) => (
          <StepRow key={step.id} step={step} live={i === visible.length - 1 && step.status === "running"} />
        ))}
      </ul>
    </div>
  );
});

export default ActivityGroup;
